import { ColumnListDto } from '@common/dto/column/list-dto';
import { ColumnSaveDto } from '@common/dto/column/save-dto';
import { FolderGetDto } from '@common/dto/folder/get-dto';
import { HttpSuccessDto } from '@common/dto/http';
import { PedigreeListDto } from '@common/dto/pedigree/list-dto';
import { PedigreeExternalLinkDto } from '@modules/pedigrees/dto/pedigree/external-link/get-dto';
import { clientTransformed } from '@common/utils/rest';
import { ItemSecurityManagementOption } from '@common/components/item-security-management';
import { FolderListDto } from '@common/dto/folder/list-dto';
import client from '@common/utils/rest';
import { UserMaintenanceProfile } from '@common/dto/user-maintenance/profile';
import { executeUI } from '@common/utils/ui-bridge';
import { FolderPrimaryListDto } from '@common/dto/folder/primary-list-dto';

const URL_PREFIX = 'api/pedigree';

const getUrl = (path: string) => {
  return `${URL_PREFIX}/${path}`;
};

export const getPedigreeExternalLink = (pedigreeId: PedigreeListDto['id']): Promise<PedigreeExternalLinkDto> => {
	return clientTransformed({
    path: getUrl('externalLink'),
    method: 'GET',
    params: {
      pedigreeId
    }
  }).then(
    (response: HttpSuccessDto<PedigreeExternalLinkDto>) => {
      return response.entity;
    },
  );
};

export const getPedigreesColumns = (): Promise<Array<ColumnListDto>> => {
	return clientTransformed({
    path: getUrl('columns'),
    method: 'GET'
  }).then(
    (response: HttpSuccessDto<Array<ColumnListDto>>) => {
      return response.entity;
    },
  );
};

export const savePedigreesColumns = (columns: Array<ColumnSaveDto>) => {
	return clientTransformed({
    path: getUrl('columns'),
    method: 'POST',
		entity: {
      columns
    },
  });
};

export type GetPedigreesRequestFiltering = {
  search?: string;
  columns: Array<ColumnListDto>;
  filterApplied: boolean;
  columnFilters: {};
  folderId?: FolderGetDto['id'];
};

export type GetPedigreesRequestPagination = {
  start: number;
  size: number;
};

export type GetPedigreesRequestParams = {
  filtering: GetPedigreesRequestFiltering;
  pagination: GetPedigreesRequestPagination;
};

export const getPedigrees = ({ filtering, pagination }: GetPedigreesRequestParams): Promise<{ data: Array<PedigreeListDto>; totalCount: number; }> => {
	return clientTransformed({
    path: getUrl('smartlist'),
    method: 'POST',
		entity: {
			...filtering,
			...pagination,
		},
  }).then(
    (response: HttpSuccessDto<{ data: Array<PedigreeListDto>; totalCount: number; }>) => {
      return response.entity;
    },
  );
};

export const importPedigree = (data: { pedigreeName: string; file: File; folderId: FolderListDto['id']; }): Promise<void> => {
  return client({
    path: 'pedigreeImport',
    method: 'POST',
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    entity: {
      name: data.pedigreeName,
      folderId: data.folderId,
      fileUpload: data.file,
    }
  }).then((res: HttpSuccessDto<string>) => {
    const errorPrefix = "ERROR:";

    if (res.entity?.startsWith(errorPrefix)) {
      throw { entity: res.entity.replace(errorPrefix, "") };
    }
  })
};

export const getPedigreeSecurity = (pedigreeId: PedigreeListDto['id']) => {
  return clientTransformed({
    path: getUrl('security'),
    method: 'GET',
		params: {
			pedigreeId
		},
  }).then(
    (response: HttpSuccessDto<{ items: Array<ItemSecurityManagementOption>; overridden: boolean }>) => {
      return response.entity;
    },
  );
};

export const updatePedigreeSecurity = (securityOptions: Array<ItemSecurityManagementOption>, pedigreeId: PedigreeListDto['id']) => {
  return clientTransformed({
    path: getUrl('security'),
    method: 'POST',
		params: {
			pedigreeId
		},
    entity: securityOptions
  })
};

export const openPedigreeDatasheet = (pedigreeId: PedigreeListDto['id']) => {
  executeUI('PEDIGREE', 'openDatasheet', pedigreeId, '');
}

export const openPedigreeFromPedigrees = (pedigreeId: PedigreeListDto['id']) => {
    executeUI('PEDIGREE', 'openPedigree', pedigreeId, '');
}

export const applyPedigreesCurrentColumnsToAllUsers = (columns: Array<ColumnListDto>) => {
	return clientTransformed({
    path: getUrl('columns'),
    method: 'POST',
		entity: {
      columns,
      action: "APPLY_ALL"
    },
  });
};

export const applyPedigreesCurrentColumnsToSpecificUsers = (columns: Array<ColumnListDto>, profilesFormat: Array<UserMaintenanceProfile['name']>) => {
	return clientTransformed({
    path: getUrl('columns'),
    method: 'POST',
		entity: {
      columns,
      profiles: profilesFormat,
      action: "APPLY_PROFILES"
    },
  });
};

export const setPedigreesDefaultColumnsForToAllUsers = (columns: Array<ColumnListDto>) => {
	return clientTransformed({
    path: getUrl('columns'),
    method: 'POST',
		entity: {
      columns,
      action: "DEFAULT_ALL"
    },
  });
};

export const setPedigreesDefaultColumnsForSpecificUsers = (columns: Array<ColumnListDto>, profilesFormat: Array<UserMaintenanceProfile['name']>) => {
	return clientTransformed({
    path: getUrl('columns'),
    method: 'POST',
		entity: {
      columns,
      profiles: profilesFormat,
      action: "DEFAULT_PROFILES"
    },
  });
};

export const movePedigrees = ({ folderId, pedigreeIds }: { folderId: FolderPrimaryListDto['id']; pedigreeIds: Array<PedigreeListDto['id']> }) => {
  return clientTransformed({
    path: getUrl('move'),
    method: 'POST',
    params: {
      folderId
    },
		entity: pedigreeIds,
  });
}

export const deletePedigrees = (pedigreeIds: Array<PedigreeListDto['id']>)=> {
  return clientTransformed({
    path: getUrl('delete'),
    method: 'POST',
		entity: pedigreeIds,
  });
};

export const deletePedigreeShortcut = (pedigreeShortcutId: string)=> {
  return clientTransformed({
    path: getUrl('deleteShortcut'),
    method: 'POST',
		params: {
      shortcutId: pedigreeShortcutId
    },
  });
};

export const restorePedigreesColumnsToDefault = () => {
	return clientTransformed({
    path: getUrl('columns'),
    method: 'GET',
    params: {
      default: true
    },
  }).then(
    (response: HttpSuccessDto<Array<ColumnListDto>>) => {
      return response.entity;
    },
  );
};

export const exportPedigree = async (pedigree: { id: PedigreeListDto['id'] }): Promise<Blob> => {
  return client({
    path: getUrl('export'),
    method: 'GET',
		params: {
			pedigreeId: pedigree.id
		},
  }).then((response: HttpSuccessDto<Blob>) => {
      return new Blob([response.entity])
  })
};

export const copyPedigree = (data: { familyId: PedigreeListDto['id']; folderId: FolderPrimaryListDto['id']; pedigreeName: string }) => {
  return clientTransformed({
    path: getUrl('copy'),
    method: 'POST',
    params: {
      familyId: data.familyId,
      newFolderId: data.folderId,
      pedigreeName: data.pedigreeName,
    }
  });
};

export const renamePedigree = (data: { familyId: PedigreeListDto['id']; folderId: FolderPrimaryListDto['id']; pedigreeName: string; renameIndividuals: boolean; }) => {
  return clientTransformed({
    path: getUrl('rename'),
    method: 'POST',
    params: {
      familyId: data.familyId,
      changeName: data.renameIndividuals,
      folderId: data.folderId,
      newName: data.pedigreeName
    },
  });
};

export const createPedigreeShortcut = (data: { familyId: PedigreeListDto['id']; folderId: FolderPrimaryListDto['id']; shortcutName: string }) => {
  return clientTransformed({
    path: getUrl('shortcut'),
    method: 'POST',
    params: data,
  });
};

export const callAuditPedigreeView = (pedigreeId: PedigreeListDto['id']) => {
    return clientTransformed({
        path: getUrl('audit'),
        method: 'POST',
        params: {
            pedigreeId
        }
    })
};
